/* eslint no-shadow: ["error", { "allow": ["state", "getters"] }] */
/* eslint object-curly-newline: ["error", { "multiline": true }] */
/* eslint no-param-reassign: ["error", { "props": false }] */
import axios from 'axios';
import qs from 'qs';

// initial state
const state = {
  user: null,
  attendee: null,
  verification_code: null,
  access_code: null,
  email: null,
  userId: null,
  attendeeId: null,
  error: false,
  message: null,
  loaders: {},
};

// getters
const getters = {
  user: (state) => state.user,
  attendee: (state) => state.attendee,
  access_code: (state) => state.access_code,
  verification_code: (state) => state.verification_code,
  email: (state) => state.email,
  attendeeId: (state) => state.attendeeId,
  userId: (state) => state.userId,
  error: (state) => state.error,
  message: (state) => state.message,
  isAuthenticated: (state) => !!state.user,
  loaders: (state) => state.loaders,
};

// actions
const actions = {
  reset({ commit }, email) {
    return axios.post('/api/reset/password/', qs.stringify(email))
      .then(() => {
        commit('SET_PROPERTY', { key: 'error', value: false });
        commit('SET_PROPERTY', { key: 'message', value: 'Please check your inbox for a password update link and instructions' });
      })
      .catch((error) => {
        commit('SET_ERROR_MESSAGE', 'Please contact your regional manager for assistance with member portal access');
        throw new Error(error);
      });
  },
  validateVerifyCode({ commit }, payload) {
    return axios.post('/api/verify/validateCode/', qs.stringify(payload))
      .then(({ data }) => {
        commit('SET_VERIFY_DATA', data);
      })
      .catch((error) => {
        commit('SET_VERIFY_DATA', {
          verification_code: null,
          email: null,
          userId: null,
        });
        commit('SET_ERROR_MESSAGE', 'Verification code invalid. Please request a new reset link using the form below.');
        throw new Error(error);
      });
  },
  verify({ commit }, credentials) {
    return axios.post('/api/verify/verifyAccount/', qs.stringify(credentials))
      .then(({ data }) => {
        commit('SET_USER_DATA', data);
      })
      .catch((error) => {
        commit('SET_ERROR_MESSAGE', 'Verification code invalid. Please request a new reset link using the form below.');
        throw new Error(error);
      });
  },
  login({ commit, dispatch }, credentials) {
    return axios.post('/api/login/', qs.stringify(credentials))
      .then(({ data }) => {
        console.log(typeof data);
        if (typeof data === 'string') {
          commit('SET_ERROR_MESSAGE', 'Your credentials could not be verified. Please use the Reset Password link to restore access.');
          throw new Error();
        } else {
          commit('SET_USER_DATA', data);
          dispatch('sessionSetup');
        }
      })
      .catch(() => {
        commit('SET_ERROR_MESSAGE', 'Your credentials could not be verified. Please try again.');
        throw new Error();
      });
  },
  logout({ commit }) {
    commit('LOGOUT');
  },
  sessionSetup({ dispatch, state }) {
    dispatch('main/setProperty', { key: 'member', value: state.user.member }, { root: true });
    dispatch('main/setProperty', { key: 'member_id', value: state.user.member.id }, { root: true });
    dispatch('main/setProperty', { key: 'permissions', value: state.user.permissions }, { root: true });
    dispatch('main/setProperty', { key: 'children', value: state.user.children }, { root: true });
  },
  attendeeSessionSetup({ dispatch, state }) {
    dispatch('main/setProperty', { key: 'meetingAttendee', value: true }, { root: true });
    dispatch('main/setProperty', { key: 'attendee_id', value: state.attendee.attendee_id }, { root: true });
    dispatch('main/setProperty', { key: 'member_id', value: state.attendee.member_id }, { root: true });
    dispatch('main/setProperty', { key: 'permissions', value: state.attendee.permissions }, { root: true });
    dispatch('main/setProperty', { key: 'children', value: state.attendee.children }, { root: true });
  },
  setProperty({ commit }, payload) {
    commit('SET_PROPERTY', payload);
  },
  loginFuelEmployee({ commit, dispatch }, token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    return axios.post('/api/login/fuelEmployee/', qs.stringify({ token }))
      .then(({ data }) => {
        commit('SET_USER_DATA', data);
        dispatch('main/setProperty', { key: 'fuelEmployee', value: true }, { root: true });
        dispatch('sessionSetup');
      })
      .catch((error) => {
        commit('SET_ERROR_MESSAGE', 'Your credentials could not be verified. Please try again.');
        throw new Error(error);
      });
  },
  authorizeMeetingAccess({ commit, dispatch, state }, email) {
    return axios.post('/api/authorize/meetingAccess/', qs.stringify({ email, access_code: state.access_code }))
      .then(({ data }) => {
        commit('SET_ATTENDEE_DATA', data);
        dispatch('main/setProperty', { key: 'meetingAttendee', value: true }, { root: true });
        dispatch('attendeeSessionSetup');
        return data.agenda_id;
      })
      .catch((error) => {
        commit('SET_ERROR_MESSAGE', 'We were unable to authorize your access at this time.');
        throw new Error(error);
      });
  },
  reloadAttendeeData({ commit, dispatch }, payload) {
    commit('SET_ATTENDEE_DATA', JSON.parse(payload));
    dispatch('attendeeSessionSetup');
  },
  reloadUserData({ commit, dispatch }, payload) {
    commit('SET_USER_DATA', JSON.parse(payload));
    dispatch('sessionSetup');
  },
  changeMemberBeingViewed({ commit, dispatch, state }, payload) {
    dispatch('main/setProperty', { key: 'globalLoading', value: true }, { root: true });
    axios.post(`${CONFIG.get('BASE_URL')}/api/member/info/changeMemberBeingViewed/`, qs.stringify(payload))
      .then(({ data }) => {
        commit('SET_USER_DATA', { ...state.user, ...data });
        dispatch('sessionSetup');
        axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
        location.reload();
      })
      .catch((error) => {
        commit('TOGGLE_LOADING', { key: 'memberChange', value: false });
        console.error(error);
      });
  },
};

// mutations
const mutations = {
  SET_USER_DATA(state, userData) {
      state.user = userData;
      localStorage.setItem('user', JSON.stringify(state.user));
      axios.defaults.headers.common.Authorization = `Bearer ${state.user.token}`;
  },
  SET_ATTENDEE_DATA(state, attendeeData) {
    state.attendee = attendeeData;
    localStorage.setItem('attendee', JSON.stringify(state.attendee));
    axios.defaults.headers.common.Authorization = `Bearer ${state.attendee.token}`;
  },
  SET_VERIFY_DATA(state, verifyData) {
    state.verification_code = verifyData.verification_code;
    state.email = verifyData.email;
    state.userId = verifyData.userId;
  },
  LOGOUT() {
    localStorage.removeItem('user');
    localStorage.removeItem('attendee');
    localStorage.clear();
    return axios.get('/api/logout/logout')
      .then(() => {
        location.reload();
      });
  },
  SET_PROPERTY(state, payload) {
    state[payload.key] = payload.value;
  },
  TOGGLE_LOADING(state, payload) {
    state.loaders[payload.key] = payload.value;
  },
  SET_ERROR_MESSAGE(state, payload) {
    state.error = true;
    state.message = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
